import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check, Clock, Thermometer, Wallet } from "lucide-react";

import { SectionHeading } from "../components/SectionHeading";
import { AanbouwCalculator } from "../components/AanbouwCalculator";
import { CtaBand } from "../components/CtaBand";
import { dienstGalerij } from "../lib/dienst-galerij";
import { SITE_URL, ogImageDefault, ogImageMeta } from "../lib/seo";

const title = "Wat kost een aanbouw in 2026? Prijs per m² + rekenhulp";
// Richtprijzen en projectafhankelijkheid blijven ook in de zoeksnippet duidelijk.
const description =
  "Aanbouw: richtprijs € 1.800–€ 3.200 per m² compleet, € 1.200–€ 1.700 casco. Vergelijk kosten en rekenhulp; fundering, maat en afwerking bepalen de offerte.";
// Kortere varianten zodat social previews niet worden afgekapt
// (og:title ≤ 60, og:description ≤ 110 tekens).
const socialTitle = "Aanbouw kosten 2026: prijs per m² in Dordrecht";
const socialDescription =
  "€ 1.800–€ 3.200 per m² compleet, € 1.200–€ 1.700 casco. Reken je uitbouw direct uit met onze tool.";

// Directe antwoorden op "aanbouw prijs per meter" — per gangbare maat.
const prijsPerMaat = [
  { maat: "10 m²", casco: "€ 12.000 – € 17.000", compleet: "€ 18.000 – € 32.000" },
  { maat: "15 m²", casco: "€ 18.000 – € 25.500", compleet: "€ 27.000 – € 48.000" },
  { maat: "20 m²", casco: "€ 24.000 – € 34.000", compleet: "€ 36.000 – € 60.000" },
  { maat: "30 m²", casco: "€ 34.000 – € 48.000", compleet: "€ 51.000 – € 84.000" },
];

// "aanbouw casco": wat zit er wel en niet in.
const cascoInbegrepen = [
  "Fundering op staal of palen, inclusief grondwerk",
  "Ruwbouw: houtskeletbouw-elementen of gemetselde wanden",
  "Dakconstructie, dakbeschot en waterdichte dakbedekking",
  "Isolatie in wand, vloer en dak volgens Bbl-eisen",
  "Plaatsing van kozijnen, schuifpui en beglazing",
  "Wind- en waterdichte aansluiting op de bestaande woning",
];

const cascoNietInbegrepen = [
  "Elektra, water, cv en vloerverwarming",
  "Stucwerk of gipsplaten afwerking binnen",
  "Vloerafwerking, plinten en schilderwerk",
  "Keuken, sanitair en inbouwmeubels",
  "Bestrating en tuinherstel rondom de aanbouw",
];


const richtprijzen = [
  {
    type: "Houtskeletbouw aanbouw (prefab)",
    prijs: "€ 1.800 – € 2.600 per m²",
    toelichting:
      "Snelle bouwtijd, weinig bouwoverlast en uitstekend te isoleren. Gunstig bij een uitbouw van 10 tot 20 m².",
  },
  {
    type: "Traditioneel metselwerk (steens)",
    prijs: "€ 2.200 – € 3.200 per m²",
    toelichting:
      "Sluit visueel naadloos aan op bestaand metselwerk, maar kent een langere bouwtijd en hogere arbeidskosten.",
  },
  {
    type: "Serre of glazen uitbouw",
    prijs: "€ 2.500 – € 3.800 per m²",
    toelichting:
      "De prijs wordt vooral bepaald door het glasoppervlak, de kozijnen en de zonwering.",
  },
  {
    type: "Alleen ruwbouw (casco)",
    prijs: "€ 1.200 – € 1.700 per m²",
    toelichting:
      "Fundering, wanden en dak. Afbouw, installaties en afwerking regel je zelf of later.",
  },
];

const kostenposten = [
  {
    post: "Vergunning en leges",
    bedrag: "€ 0 – € 1.500",
    uitleg:
      "Een uitbouw tot 4 meter diep aan de achtergevel is vaak vergunningvrij. Is een omgevingsvergunning wél nodig, reken dan op leges plus tekenwerk.",
  },
  {
    post: "Constructieberekening",
    bedrag: "€ 500 – € 1.200",
    uitleg:
      "Nodig zodra je een draagmuur doorbreekt of een stalen lateibalk plaatst.",
  },
  {
    post: "Fundering",
    bedrag: "€ 200 – € 600 per m²",
    uitleg:
      "In Dordrecht en omgeving is de bodem vaak slap; palen of een zwaardere fundering verhogen de prijs merkbaar.",
  },
  {
    post: "Sloop en grondwerk",
    bedrag: "€ 1.000 – € 3.000",
    uitleg: "Verwijderen van bestaande bestrating, kozijnen of een oude serre, inclusief afvoer.",
  },
  {
    post: "Kozijnen en schuifpui",
    bedrag: "€ 2.500 – € 9.000",
    uitleg:
      "Een houten of aluminium schuifpui met HR++ of triple glas is vaak de grootste losse post.",
  },
  {
    post: "Installaties (elektra, water, vloerverwarming)",
    bedrag: "€ 2.000 – € 6.000",
    uitleg: "Afhankelijk van of de aanbouw een keuken, badkamer of alleen woonruimte wordt.",
  },
  {
    post: "Afwerking (stucwerk, vloer, schilderwerk)",
    bedrag: "€ 250 – € 500 per m²",
    uitleg: "Hier valt het meest te besparen door zelf te schilderen of de vloer te leggen.",
  },
];

const factoren = [
  "Oppervlakte: hoe groter de uitbouw, hoe lager de prijs per m² — vaste kosten zoals fundering en aansluitingen verdelen zich over meer meters.",
  "Bereikbaarheid: een smalle achterom of een tuin zonder achteringang betekent handmatig materiaal sjouwen en dus meer uren.",
  "Dakvorm: een plat dak is aanzienlijk voordeliger dan een lessenaars- of schuin dak met dakramen.",
  "Isolatie-eisen: extra isolatie kost vooraf geld, maar verlaagt je energierekening en kan meetellen voor subsidie.",
  "Afwerkniveau: standaard stucwerk en een laminaatvloer versus maatwerk kasten en gietvloer scheelt al snel duizenden euro's.",
];

const vergelijking = [
  {
    kenmerk: "Kosten per m²",
    hsb: "€ 1.800 – € 2.600",
    metselwerk: "€ 2.200 – € 3.200",
  },
  {
    kenmerk: "Isolatiewaarde",
    hsb: "Standaard hoog (Rc 4,5 – 6,0) door dikke isolatie in het element",
    metselwerk: "Goed (Rc 3,5 – 4,5), afhankelijk van de spouwbreedte",
  },
  {
    kenmerk: "Bouwtijd",
    hsb: "4 – 6 weken wind- en waterdicht",
    metselwerk: "8 – 12 weken, gevoelig voor vorst en regen",
  },
  {
    kenmerk: "Overlast in de tuin",
    hsb: "Kort: elementen worden in enkele dagen gemonteerd",
    metselwerk: "Langer: steigerwerk, mortel en stenen op locatie",
  },
  {
    kenmerk: "Aansluiting bestaande gevel",
    hsb: "Afwerking met stucwerk, hout of steenstrips",
    metselwerk: "Naadloos in dezelfde steen en verband",
  },
  {
    kenmerk: "Ontwerpvrijheid tijdens bouw",
    hsb: "Beperkt — maatvoering ligt vroeg vast",
    metselwerk: "Groot — wijzigen kan tot laat in het traject",
  },
];

// Lokale kostenfactoren per plaats in ons werkgebied — indicatief, op basis
// van projecten die wij in de Drechtsteden hebben uitgevoerd.
const regioFactoren = [
  {
    plaats: "Dordrecht",
    woningtype: "Jaren-30 woningen, naoorlogse rijwoningen en binnenstadspanden",
    aandachtspunt:
      "Slappe veen- en kleibodem: vaak fundering op palen. In de historische binnenstad en bij beschermd stadsgezicht is een omgevingsvergunning nodig.",
    meerkosten: "+ € 3.000 – € 8.000 (paalfundering)",
    link: "/timmerman-dordrecht",
  },
  {
    plaats: "Zwijndrecht",
    woningtype: "Rijwoningen en twee-onder-een-kappers uit de jaren 60–80",
    aandachtspunt:
      "Meestal ruime achtertuin met achterom, waardoor prefab elementen goed aan te voeren zijn. Vaak binnen de vergunningvrije maten.",
    meerkosten: "+ € 0 – € 2.000",
    link: "/timmerman-zwijndrecht",
  },
  {
    plaats: "Papendrecht",
    woningtype: "Naoorlogse rij- en hoekwoningen, veel platte aanbouwen",
    aandachtspunt:
      "Bestaande berging of oude serre slopen komt vaak terug in de begroting, inclusief afvoer en herstel bestrating.",
    meerkosten: "+ € 1.000 – € 3.000 (sloop en afvoer)",
    link: "/timmerman-papendrecht",
  },
  {
    plaats: "Sliedrecht",
    woningtype: "Dijkwoningen en jaren-30 woningen langs de Merwede",
    aandachtspunt:
      "Bij dijkwoningen spelen hoogteverschil en soms een keurzone van het waterschap; dat vraagt extra grondwerk en overleg.",
    meerkosten: "+ € 2.000 – € 6.000 (grondwerk en niveauverschil)",
    link: "/timmerman-sliedrecht",
  },
];


// Kosten per gangbare uitbouwdiepte — de meest gezochte maten (3 en 4 meter),
// gerekend over een gemiddelde gevelbreedte van 4 tot 5 meter.
const dieptes = [
  {
    diepte: "Uitbouw 2,5 meter diep",
    oppervlakte: "circa 10 – 13 m²",
    compleet: "€ 18.000 – € 40.000",
    casco: "€ 12.000 – € 22.000",
    vergunning:
      "Vrijwel altijd vergunningvrij aan de achtergevel; tot 2,5 meter geldt de lichtste toets.",
  },
  {
    diepte: "Uitbouw 3 meter diep",
    oppervlakte: "circa 12 – 15 m²",
    compleet: "€ 22.000 – € 48.000",
    casco: "€ 14.500 – € 25.500",
    vergunning:
      "Meestal vergunningvrij, maar boven 2,5 meter diep geldt een extra toets op het achtererfgebied en de bebouwing.",
  },
  {
    diepte: "Uitbouw 4 meter diep",
    oppervlakte: "circa 16 – 20 m²",
    compleet: "€ 29.000 – € 64.000",
    casco: "€ 19.000 – € 34.000",
    vergunning:
      "4 meter is de grens van de vergunningvrije regeling; blijf je eronder, dan vervalt het vergunningstraject.",
  },
  {
    diepte: "Uitbouw dieper dan 4 meter",
    oppervlakte: "vanaf 20 m²",
    compleet: "€ 36.000 en hoger",
    casco: "€ 24.000 en hoger",
    vergunning:
      "Altijd een omgevingsvergunning nodig; reken op leges, tekenwerk en een constructieberekening.",
  },
];

const vergunningvrijChecklist = [
  "De uitbouw staat aan de achterkant of zijkant van je woning, in het achtererfgebied — niet aan de voorzijde of langs openbaar gebied.",
  "De diepte is maximaal 4 meter gemeten vanaf de oorspronkelijke achtergevel; bij meer dan 2,5 meter geldt een extra toets.",
  "De hoogte blijft onder 4 meter en niet hoger dan 30 cm boven de vloer van de eerste verdieping.",
  "Maximaal 50 procent van het achtererfgebied wordt bebouwd, met een bebouwingsmaximum afhankelijk van de perceelgrootte.",
  "Je woning is geen monument en ligt niet in beschermd stadsgezicht — in de historische binnenstad van Dordrecht is dat vaak wél het geval.",
  "Er geldt geen afwijkende regel uit het omgevingsplan van jouw gemeente (Dordrecht, Zwijndrecht, Papendrecht of Sliedrecht).",
  "Voor de constructie en brandveiligheid geldt het Besluit bouwwerken leefomgeving; ook vergunningvrij moet je daaraan voldoen.",
];

const besparingen = [
  {
    titel: "Kies een eenvoudige vorm",
    besparing: "€ 2.000 – € 5.000",
    uitleg:
      "Een rechthoekige plattegrond met plat dak vraagt minder uren, minder materiaal en geen extra dakdetails.",
  },
  {
    titel: "Houd installaties dichtbij",
    besparing: "€ 1.000 – € 2.500",
    uitleg:
      "Sluit keuken, water en elektra aan in de buurt van bestaande leidingen; lange tracés en doorvoeren kosten uren.",
  },
  {
    titel: "Doe afwerking zelf",
    besparing: "€ 3.000 – € 7.000",
    uitleg:
      "Sloopwerk, schilderen en de vloer leggen zijn goed zelf te doen. Wij leveren dan casco of net vóór de afwerking op.",
  },
  {
    titel: "Combineer met isolatiewerk",
    besparing: "tot € 4.000 subsidie",
    uitleg:
      "Isoleer tegelijk het bestaande dak of de vloer; via de ISDE-regeling krijg je een deel van die kosten terug.",
  },
  {
    titel: "Blijf binnen vergunningvrij",
    besparing: "€ 800 – € 2.000",
    uitleg:
      "Zonder vergunningstraject vervallen leges, constructieberekeningen en tekenwerk voor de aanvraag.",
  },
  {
    titel: "Bouw buiten het hoogseizoen",
    besparing: "3 – 6 weken sneller",
    uitleg:
      "In het najaar en de winter is de planning ruimer, wat aanlooptijd scheelt en soms een scherpere prijs oplevert.",
  },
];


const faq = [
  {
    vraag: "Hoe bereken ik de aanbouw kosten per m²?",
    antwoord:
      "Neem de richtprijs per m² van je bouwmethode — € 1.800 tot € 2.600 voor houtskeletbouw, € 2.200 tot € 3.200 voor metselwerk — en vermenigvuldig die met het aantal vierkante meters. Tel daarna de losse posten op die voor jou gelden, zoals vergunning, extra fundering of een grote schuifpui. Met de rekenhulp op deze pagina doe je dat in één keer.",
  },
  {
    vraag: "Wat is de aanbouw prijs per meter?",
    antwoord:
      "Reken in 2026 op € 1.800 tot € 3.200 per m² voor een complete aanbouw en € 1.200 tot € 1.700 per m² casco. Een aanbouw van 15 m² komt daarmee op € 27.000 tot € 48.000 compleet, of € 18.000 tot € 25.500 casco. Hoe groter de uitbouw, hoe lager de prijs per meter, omdat vaste kosten zoals fundering en aansluitingen over meer meters worden verdeeld.",
  },
  {
    vraag: "Wat kost een aanbouw casco per m²?",
    antwoord:
      "Casco kost € 1.200 tot € 1.700 per m². Daarvoor leveren we fundering, ruwbouw, dak, isolatie en kozijnen wind- en waterdicht op. Installaties, stucwerk, vloer en schilderwerk zitten er niet in; die regel je zelf of later. Zo bespaar je vaak 30 tot 40 procent op de totale kosten.",
  },
  {
    vraag: "Wat kost een aanbouw in Dordrecht?",
    antwoord:
      "In Dordrecht reken je op dezelfde richtprijs van € 1.800 tot € 3.200 per m² compleet, met één belangrijke opslag: door de slappe veen- en kleibodem is een fundering op palen hier vaak nodig. Dat kost doorgaans € 3.000 tot € 8.000 extra. Staat je woning in de historische binnenstad of in beschermd stadsgezicht, dan vervalt de vergunningvrije regeling en komt er een omgevingsvergunning bij. In Zwijndrecht, Papendrecht en Sliedrecht spelen andere posten, zoals sloop van een bestaande berging of grondwerk bij dijkwoningen.",
  },
  {
    vraag: "Wat kost een aanbouw van 20 m²?",
    antwoord:
      "Voor een complete uitbouw van 20 m² inclusief fundering, schuifpui, isolatie en afwerking reken je in de praktijk op € 40.000 tot € 60.000. Casco ligt dat rond € 25.000 tot € 34.000.",
  },

  {
    vraag: "Wat kost een uitbouw van 3 meter diep?",
    antwoord:
      "Bij een gevelbreedte van 4 tot 5 meter komt een uitbouw van 3 meter diep neer op 12 tot 15 m². Compleet reken je op € 22.000 tot € 48.000, casco op € 14.500 tot € 25.500. Boven 2,5 meter diep geldt een extra toets op het achtererfgebied, maar meestal blijft de uitbouw vergunningvrij.",
  },
  {
    vraag: "Wat kost een uitbouw van 4 meter diep?",
    antwoord:
      "Een uitbouw van 4 meter diep levert circa 16 tot 20 m² extra woonruimte op: € 29.000 tot € 64.000 compleet en € 19.000 tot € 34.000 casco. 4 meter is precies de grens van de vergunningvrije regeling; bouw je dieper, dan is een omgevingsvergunning met tekenwerk en constructieberekening nodig.",
  },
  {
    vraag: "Is prefab of traditioneel metselwerk goedkoper?",
    antwoord:
      "Prefab houtskeletbouw is meestal 15 tot 25 procent voordeliger en veel sneller klaar. Traditioneel metselwerk kost meer, maar sluit qua uitstraling naadloos aan bij een gemetselde woning.",
  },
  {
    vraag: "Heb ik een vergunning nodig voor een uitbouw?",
    antwoord:
      "Een uitbouw aan de achtergevel tot 4 meter diep is in veel gevallen vergunningvrij, mits het bebouwingspercentage van het achtererfgebied klopt. Bij twijfel controleren we dit samen met de gemeente voordat we starten.",
  },
  {
    vraag: "Verhoogt een aanbouw de waarde van mijn woning?",
    antwoord:
      "Ja. Extra woonoppervlak telt mee in de taxatie. In de regel wordt 60 tot 80 procent van de investering terugverdiend in woningwaarde, plus dagelijks meer wooncomfort.",
  },
  {
    vraag: "Hoelang duurt het bouwen van een aanbouw?",
    antwoord:
      "Een prefab uitbouw staat vaak binnen 4 tot 6 weken wind- en waterdicht. Traditioneel metselwerk duurt doorgaans 8 tot 12 weken, exclusief voorbereiding en vergunningstraject.",
  },
  {
    vraag: "Wanneer is een aanbouw vergunningvrij in Dordrecht?",
    antwoord:
      "Een aanbouw aan de achtergevel tot 4 meter diep en maximaal 4 meter hoog is vaak vergunningvrij, zolang niet meer dan 50 procent van het achtererfgebied wordt bebouwd. In de historische binnenstad van Dordrecht en bij monumenten of beschermd stadsgezicht geldt die vrijstelling niet en is een omgevingsvergunning nodig. Wij controleren dit vooraf via het Omgevingsloket en het omgevingsplan van je gemeente.",
  },
  {
    vraag: "Hoe kan ik het meest besparen op een aanbouw?",
    antwoord:
      "De grootste besparingen zitten in een eenvoudige rechthoekige vorm met plat dak (€ 2.000 – € 5.000), zelf de afwerking doen zoals schilderen en de vloer leggen (€ 3.000 – € 7.000) en binnen de vergunningvrije maten blijven (€ 800 – € 2.000 aan leges en tekenwerk). Combineer je de aanbouw met isolatie van dak of vloer, dan is via de ISDE-regeling tot € 4.000 subsidie mogelijk.",
  },
];


const aanbouwAfbeeldingen = dienstGalerij["aanbouw"] ?? [];

const recenteProjecten = [
  {
    titel: "Houtskeletbouw aanbouw in Dordrecht",
    locatie: "Dordrecht",
    omschrijving:
      "Prefab aanbouw van 18 m² met plat dak, aluminium schuifpui en HR++ glas. Wand- en dakelementen werden in de fabriek gemaakt en binnen vijf weken gemonteerd, inclusief fundering en afwerking.",
    afbeelding: aanbouwAfbeeldingen[0],
    kenmerken: ["18 m²", "Prefab houtskeletbouw", "Plat dak"],
  },
  {
    titel: "Traditionele uitbouw in Sliedrecht",
    locatie: "Sliedrecht",
    omschrijving:
      "Gemetselde achteraanbouw van 12 m² die qua steen en voegwerk naadloos aansloot op de bestaande jaren-30 woning. Inclusief nieuwe kozijnen, vloerverwarming en stucwerk.",
    afbeelding: aanbouwAfbeeldingen[1],
    kenmerken: ["12 m²", "Metselwerk", "Kozijnen + vloerverwarming"],
  },
  {
    titel: "Woonuitbreiding met serre in Zwijndrecht",
    locatie: "Zwijndrecht",
    omschrijving:
      "Lichte glazen aanbouw van 14 m² als verlenging van de woonkamer. De houten kozijnen en zonwering maken het een comfortabele leefruimte het hele jaar door.",
    afbeelding: aanbouwAfbeeldingen[2],
    kenmerken: ["14 m²", "Glazen serre", "Houten kozijnen"],
  },
];


export const Route = createFileRoute("/aanbouw-kosten")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: socialTitle },
      { property: "og:description", content: socialDescription },
      { name: "twitter:title", content: socialTitle },
      { name: "twitter:description", content: socialDescription },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}/aanbouw-kosten` },
      ...ogImageMeta(ogImageDefault, "Aanbouw door Fedor Timmerwerken uit Dordrecht"),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/aanbouw-kosten` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Aanbouw kosten: wat kost een uitbouw in 2026?",
          description,
          inLanguage: "nl-NL",
          image: ogImageDefault,
          author: { "@type": "Organization", name: "Fedor Timmerwerken" },
          publisher: {
            "@type": "Organization",
            name: "Fedor Timmerwerken",
            logo: { "@type": "ImageObject", url: ogImageDefault },
          },
          about: { "@id": `${SITE_URL}/#business` },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${SITE_URL}/aanbouw-kosten`,
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((item) => ({
            "@type": "Question",
            name: item.vraag,
            acceptedAnswer: { "@type": "Answer", text: item.antwoord },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HomeAndConstructionBusiness",
          "@id": `${SITE_URL}/#business`,
          name: "Fedor Timmerwerken",
          url: `${SITE_URL}/`,
          image: ogImageDefault,
          telephone: "+31628130786",
          email: "info@fedortimmerwerken.nl",
          priceRange: "€€",
          currenciesAccepted: "EUR",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Sikkelstraat 50",
            postalCode: "3319 LJ",
            addressLocality: "Dordrecht",
            addressRegion: "Zuid-Holland",
            addressCountry: "NL",
          },
          areaServed: [
            { "@type": "City", name: "Dordrecht" },
            { "@type": "AdministrativeArea", name: "Drechtsteden" },
          ],
          makesOffer: {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Aanbouw en uitbouw bouwen",
              serviceType: "Aanbouw / uitbouw",
            },
            priceCurrency: "EUR",
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              priceCurrency: "EUR",
              minPrice: 1800,
              maxPrice: 3200,
              unitText: "m²",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Diensten", item: `${SITE_URL}/diensten` },
            {
              "@type": "ListItem",
              position: 3,
              name: "Aanbouw kosten",
              item: `${SITE_URL}/aanbouw-kosten`,
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Gerealiseerde aanbouwprojecten Fedor Timmerwerken",
          itemListElement: recenteProjecten.map((project, index) => ({
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@type": "ImageObject",
              name: project.titel,
              description: project.omschrijving,
              contentUrl: project.afbeelding,
              image: project.afbeelding,
              contentLocation: {
                "@type": "City",
                name: project.locatie,
              },
              creator: {
                "@type": "HomeAndConstructionBusiness",
                name: "Fedor Timmerwerken",
                url: SITE_URL,
              },
            },
          })),
        }),
      },
    ],
  }),
  component: AanbouwKostenPage,
});

function AanbouwKostenPage() {
  return (
    <>
      <div className="py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <Link
            to="/diensten/$slug"
            params={{ slug: "aanbouw" }}
            className="mx-auto flex w-fit items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80"
          >
            <ArrowRight className="h-4 w-4 rotate-180" aria-hidden="true" />
            Terug naar aanbouw
          </Link>

          <SectionHeading
            as="h1"
            eyebrow="Kostengids"
            title="Aanbouw kosten: wat kost een uitbouw in 2026?"
            description="Een eerlijk overzicht van richtprijzen per m², de verschillen tussen prefab en traditioneel metselwerk en alle kostenposten waar je rekening mee houdt — samengesteld door Fedor Timmerwerken uit Dordrecht."
            align="center"
            className="mx-auto"
          />

          <div className="mx-auto mt-10 sm:mt-14 max-w-3xl space-y-14">
            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Kort antwoord: de gemiddelde kosten
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Een complete aanbouw kost in Nederland gemiddeld{" "}
                <strong className="text-foreground">€ 1.800 tot € 3.200 per m²</strong>, inclusief
                fundering, ruwbouw, isolatie, kozijnen en afwerking. Voor een gebruikelijke uitbouw
                van 15 m² komt dat neer op ongeveer € 30.000 tot € 48.000. Kies je voor casco, dan
                ligt de investering fors lager, maar regel je afbouw en installaties zelf.
              </p>
            </section>

            <section className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8">
              <h2 className="font-heading text-xl font-semibold text-foreground">
                Zoek je een specifieke berekening?
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                We hebben de meest gezochte varianten uitgewerkt in aparte gidsen met eigen
                rekenvoorbeelden.
              </p>
              <div className="mt-4 flex flex-col gap-2 text-sm font-semibold">
                <Link
                  to="/uitbouw-kosten-per-m2"
                  className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                >
                  Uitbouw kosten per m² (10, 15, 20 en 30 m²)
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/casco-aanbouw-kosten"
                  className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                >
                  Casco aanbouw kosten — wat zit er wel en niet in?
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/aanbouw-2-verdiepingen-kosten"
                  className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                >
                  Aanbouw over 2 verdiepingen — kosten en vergunning
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </section>

            <section id="kosten-berekenen" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Aanbouw kosten berekenen per m²
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Wil je de kosten van je aanbouw zelf berekenen? Reken met de richtprijs per m² ×
                het aantal vierkante meters, en tel daar de losse posten bij op die voor jouw
                situatie gelden (vergunning, extra fundering, schuifpui). Met de rekenhulp hieronder
                heb je in één keer een realistische bandbreedte.
              </p>
              <div className="mt-6">
                <AanbouwCalculator />
              </div>
            </section>

            <section id="prijs-per-meter" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Aanbouw prijs per meter: casco en compleet
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                De prijs per meter hangt vooral af van het afwerkniveau. Compleet betaal je{" "}
                <strong className="text-foreground">€ 1.800 – € 3.200 per m²</strong>, casco{" "}
                <strong className="text-foreground">€ 1.200 – € 1.700 per m²</strong>. Hieronder wat
                dat betekent voor de meest gevraagde maten in de regio Dordrecht.
              </p>
              <div className="mt-6 overflow-x-auto">
                <table className="w-full border-collapse text-left text-sm">
                  <caption className="sr-only">
                    Aanbouw prijs per meter voor 10, 15, 20 en 30 m², casco en compleet
                  </caption>
                  <thead>
                    <tr className="border-b border-border">
                      <th scope="col" className="py-3 pr-4 font-heading font-semibold text-foreground">
                        Oppervlak
                      </th>
                      <th scope="col" className="py-3 pr-4 font-heading font-semibold text-foreground">
                        Casco
                      </th>
                      <th scope="col" className="py-3 font-heading font-semibold text-foreground">
                        Compleet
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {prijsPerMaat.map((rij) => (
                      <tr key={rij.maat} className="border-b border-border/60">
                        <th scope="row" className="py-4 pr-4 text-left font-medium text-foreground">
                          {rij.maat}
                        </th>
                        <td className="py-4 pr-4 whitespace-nowrap text-muted-foreground">
                          {rij.casco}
                        </td>
                        <td className="py-4 whitespace-nowrap font-semibold text-primary">
                          {rij.compleet}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Bedragen zijn indicatief en inclusief btw. Bij een grotere aanbouw daalt de prijs per
                meter, omdat fundering en aansluitingen over meer vierkante meters worden verdeeld.
              </p>
            </section>

            <section id="regio" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Aanbouw kosten in Dordrecht en de Drechtsteden
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Landelijke richtprijzen zeggen weinig als je bodem, woningtype en omgevingsplan
                afwijken. In onze regio zijn dit de posten die de begroting in de praktijk het
                meest beïnvloeden — gebaseerd op de aanbouwen die wij hier hebben gebouwd.
              </p>

              <div className="mt-6 space-y-4">
                {regioFactoren.map((regio) => (
                  <div
                    key={regio.plaats}
                    className="rounded-xl border border-border/60 bg-card p-5"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="font-heading text-base font-semibold text-foreground">
                        {regio.plaats}
                      </h3>
                      <span className="shrink-0 text-sm font-bold text-primary">
                        {regio.meerkosten}
                      </span>
                    </div>
                    <p className="mt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {regio.woningtype}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {regio.aandachtspunt}
                    </p>
                    <Link
                      to={regio.link}
                      className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80"
                    >
                      Timmerman in {regio.plaats}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </div>
                ))}
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Meerkosten zijn indicaties bovenop de richtprijs per m² en gelden per project. Bij
                een vrije opname meten we ter plaatse op en controleren we de fundering en het
                omgevingsplan van jouw gemeente.
              </p>
            </section>



            <section id="casco" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Aanbouw casco laten bouwen
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Bij een <strong className="text-foreground">casco aanbouw</strong> leveren wij het
                bouwkundige werk wind- en waterdicht op: fundering, ruwbouw, dak, isolatie en
                kozijnen. Installaties en afwerking doe je zelf of laat je later doen. Dat scheelt in
                de praktijk 30 tot 40 procent op de totale kosten en geeft je ruimte om in fases te
                werken.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-border/60 bg-card p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground">
                    Wél inbegrepen bij casco
                  </h3>
                  <ul className="mt-3 space-y-2">
                    {cascoInbegrepen.map((item) => (
                      <li key={item} className="flex gap-2 text-sm text-muted-foreground">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-xl border border-border/60 bg-card p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground">
                    Niet inbegrepen (zelf regelen)
                  </h3>
                  <ul className="mt-3 space-y-2">
                    {cascoNietInbegrepen.map((item) => (
                      <li key={item} className="flex gap-2 text-sm text-muted-foreground">
                        <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Twijfel je tussen casco en compleet? We kunnen ook een tussenvorm leveren, bijvoorbeeld
                casco plus installaties, zodat je alleen het schilder- en vloerwerk zelf doet.
              </p>
            </section>


            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Richtprijzen per bouwmethode
              </h2>

              {/* Mobile: stacked cards */}
              <div className="mt-6 space-y-4 sm:hidden">
                {richtprijzen.map((rij) => (
                  <div
                    key={rij.type}
                    className="rounded-xl border border-border/60 bg-card p-4"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="font-heading text-base font-semibold text-foreground">
                        {rij.type}
                      </h3>
                      <span className="shrink-0 text-base font-bold text-primary">{rij.prijs}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {rij.toelichting}
                    </p>
                  </div>
                ))}
              </div>

              {/* Desktop: table */}
              <div className="mt-6 hidden overflow-x-auto sm:block">
                <table className="w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th scope="col" className="py-3 pr-4 font-heading font-semibold text-foreground">
                        Type uitbouw
                      </th>
                      <th scope="col" className="py-3 pr-4 font-heading font-semibold text-foreground">
                        Richtprijs
                      </th>
                      <th scope="col" className="py-3 font-heading font-semibold text-foreground">
                        Toelichting
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {richtprijzen.map((rij) => (
                      <tr key={rij.type} className="border-b border-border/60 align-top">
                        <td className="py-4 pr-4 font-medium text-foreground">{rij.type}</td>
                        <td className="py-4 pr-4 whitespace-nowrap font-semibold text-primary">
                          {rij.prijs}
                        </td>
                        <td className="py-4 text-muted-foreground">{rij.toelichting}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Prijzen zijn indicatief, inclusief btw en gebaseerd op projecten in de regio
                Dordrecht. Een exacte prijs volgt altijd uit een opname ter plaatse.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Prefab of traditioneel metselwerk?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Bij <strong className="text-foreground">prefab houtskeletbouw</strong> worden wanden
                en dakelementen in de fabriek gemaakt en op locatie gemonteerd. Dat scheelt weken
                bouwtijd, beperkt overlast in de tuin en levert standaard een hoge isolatiewaarde.
                Nadeel: je legt maatvoering en indeling vroeg vast, wijzigingen tijdens de bouw zijn
                lastiger.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                <strong className="text-foreground">Traditioneel metselwerk</strong> kost gemiddeld
                15 tot 25 procent meer, vooral door arbeidsuren en steigerwerk. Daar staat tegenover
                dat de gevel exact aansluit op de bestaande steen, wat bij een karakteristieke woning
                of in een beschermd stadsgezicht vaak de doorslag geeft.
              </p>

              {/* Mobiel: kaarten per kenmerk. Desktop: volledige vergelijkingstabel. */}
              <div className="mt-6 space-y-3 sm:hidden">
                {vergelijking.map((rij) => (
                  <div
                    key={rij.kenmerk}
                    className="rounded-xl border border-border/60 bg-card p-4"
                  >
                    <p className="font-heading text-sm font-semibold text-foreground">
                      {rij.kenmerk}
                    </p>
                    <dl className="mt-3 space-y-2 text-sm">
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-wider text-primary">
                          Houtskeletbouw
                        </dt>
                        <dd className="text-muted-foreground">{rij.hsb}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Metselwerk
                        </dt>
                        <dd className="text-muted-foreground">{rij.metselwerk}</dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>

              <div className="mt-6 hidden overflow-hidden rounded-xl border border-border/60 sm:block">
                <table className="w-full text-left text-sm">
                  <caption className="sr-only">
                    Vergelijking houtskeletbouw en traditioneel metselwerk op kosten, isolatie en
                    bouwtijd
                  </caption>
                  <thead className="bg-secondary/40">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-heading text-foreground">
                        Kenmerk
                      </th>
                      <th scope="col" className="px-4 py-3 font-heading text-foreground">
                        <span className="inline-flex items-center gap-2">
                          <Thermometer className="h-4 w-4 text-primary" aria-hidden="true" />
                          Houtskeletbouw (prefab)
                        </span>
                      </th>
                      <th scope="col" className="px-4 py-3 font-heading text-foreground">
                        <span className="inline-flex items-center gap-2">
                          <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
                          Traditioneel metselwerk
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {vergelijking.map((rij) => (
                      <tr key={rij.kenmerk} className="border-t border-border/60 align-top">
                        <th scope="row" className="px-4 py-4 font-medium text-foreground">
                          {rij.kenmerk}
                        </th>
                        <td className="px-4 py-4 text-muted-foreground">{rij.hsb}</td>
                        <td className="px-4 py-4 text-muted-foreground">{rij.metselwerk}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="kosten-per-diepte">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Aanbouw kosten per diepte: 3 meter en 4 meter uitbouw
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                De meeste mensen zoeken op diepte in plaats van op vierkante meters. Onderstaande
                bedragen gaan uit van een gevelbreedte van 4 tot 5 meter, inclusief fundering, dak,
                isolatie en een schuifpui. Let op: bij een uitbouw dieper dan 4 meter vervalt de
                vergunningvrije regeling.
              </p>
              <div className="mt-6 overflow-x-auto rounded-lg border border-border/60">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Diepte
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Oppervlakte
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Casco
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Compleet
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Vergunning
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dieptes.map((rij) => (
                      <tr key={rij.diepte} className="border-t border-border/60 align-top">
                        <th scope="row" className="px-4 py-4 font-medium text-foreground">
                          {rij.diepte}
                        </th>
                        <td className="px-4 py-4 text-muted-foreground">{rij.oppervlakte}</td>
                        <td className="px-4 py-4 font-semibold text-foreground">{rij.casco}</td>
                        <td className="px-4 py-4 font-semibold text-primary">{rij.compleet}</td>
                        <td className="px-4 py-4 text-muted-foreground">{rij.vergunning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Wil je het exact weten voor jouw maat? Bereken je uitbouw met de rekenhulp
                hierboven, of bekijk{" "}
                <Link
                  to="/uitbouw-kosten-per-m2"
                  className="font-semibold text-primary underline-offset-4 hover:underline"
                >
                  uitbouw kosten per m²
                </Link>{" "}
                en{" "}
                <Link
                  to="/casco-aanbouw-kosten"
                  className="font-semibold text-primary underline-offset-4 hover:underline"
                >
                  casco aanbouw kosten
                </Link>
                .
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Checklist: vergunningvrij bouwen in de Drechtsteden
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Veel aanbouwen in Dordrecht, Zwijndrecht, Papendrecht en Sliedrecht kunnen
                vergunningvrij. Loop deze punten na; zijn ze allemaal van toepassing, dan is de kans
                groot dat je zonder omgevingsvergunning kunt bouwen.
              </p>
              <ul className="mt-6 space-y-3">
                {vergunningvrijChecklist.map((punt) => (
                  <li
                    key={punt}
                    className="flex gap-3 rounded-lg border border-border/60 bg-card p-3 text-sm leading-relaxed text-muted-foreground"
                  >
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    <span>{punt}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Twijfel je? Doe de vergunningcheck via het{" "}
                <a
                  href="https://omgevingswet.overheid.nl/vergunningcheck"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary underline-offset-4 hover:underline"
                >
                  Omgevingsloket
                </a>{" "}
                of vraag het ons — we controleren het omgevingsplan van jouw gemeente voordat we een
                planning maken.
              </p>
            </section>



            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Alle kostenposten op een rij
              </h2>
              <ul className="mt-6 space-y-5">
                {kostenposten.map((post) => (
                  <li key={post.post} className="border-l-2 border-primary/40 pl-4">
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <span className="font-heading text-base font-semibold text-foreground">
                        {post.post}
                      </span>
                      <span className="text-sm font-semibold text-primary">{post.bedrag}</span>
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {post.uitleg}
                    </p>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Wat bepaalt de prijs van jouw uitbouw?
              </h2>
              <ul className="mt-4 space-y-3">
                {factoren.map((factor) => (
                  <li
                    key={factor}
                    className="flex gap-3 text-sm leading-relaxed text-muted-foreground"
                  >
                    <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{factor}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Zo bespaar je op de kosten van een aanbouw
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Met een paar keuzes vroeg in het traject scheelt een uitbouw duizenden euro's, zonder
                dat je inlevert op kwaliteit. Dit zijn de besparingen die in de praktijk het meeste
                opleveren.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {besparingen.map((item) => (
                  <div
                    key={item.titel}
                    className="rounded-xl border border-border/60 bg-card p-4 sm:p-5"
                  >
                    <div className="flex items-start gap-3">
                      <Wallet className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                      <div className="min-w-0">
                        <h3 className="font-heading text-base font-semibold text-foreground">
                          {item.titel}
                        </h3>
                        <p className="mt-1 text-sm font-semibold text-primary">{item.besparing}</p>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                          {item.uitleg}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Bekijk ook de{" "}
                <Link to="/subsidie" className="font-semibold text-primary underline-offset-4 hover:underline">
                  subsidiemogelijkheden voor isolatie
                </Link>{" "}
                — die kunnen een deel van de isolatiekosten van je aanbouw dekken.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Recente aanbouwprojecten in Dordrecht en omgeving
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Deze aanbouwen hebben wij de afgelopen jaren gerealiseerd. Ze geven een concreet beeld
                van de prijsniveaus, materialen en afwerking die passen bij de situatie in Dordrecht,
                Sliedrecht en Zwijndrecht.
              </p>

              <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {recenteProjecten.map((project) => (
                  <figure
                    key={project.titel}
                    className="group overflow-hidden rounded-xl border border-border/60 bg-card"
                  >
                    {project.afbeelding ? (
                      <img
                        src={project.afbeelding}
                        alt={project.titel}
                        loading="lazy"
                        decoding="async"
                        className="aspect-[4/3] w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : null}
                    <figcaption className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {project.kenmerken.map((kenmerk) => (
                          <span
                            key={kenmerk}
                            className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
                          >
                            {kenmerk}
                          </span>
                        ))}
                      </div>
                      <h3 className="mt-3 font-heading text-base font-semibold text-foreground">
                        {project.titel}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {project.omschrijving}
                      </p>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Veelgestelde vragen over aanbouw kosten
              </h2>
              <div className="mt-6 space-y-6">
                {faq.map((item) => (
                  <div key={item.vraag}>
                    <h3 className="font-heading text-base font-semibold text-foreground">
                      {item.vraag}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {item.antwoord}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-xl border border-border/60 bg-card p-6">
              <h2 className="font-heading text-xl font-bold tracking-tight text-foreground">
                Benieuwd naar de prijs voor jouw situatie?
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                We komen langs in Dordrecht en omgeving, meten op en leveren een gespecificeerde
                offerte per kostenpost — zonder verrassingen achteraf.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  Vraag een offerte aan
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/diensten/$slug"
                  params={{ slug: "aanbouw" }}
                  className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
                >
                  Bekijk onze aanbouwprojecten
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>

      <CtaBand />
    </>
  );
}
